import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { useAppDispatch, useAppSelector } from '../../app/hooks.js';
import { selectMeasureVertices } from '../../store/selectors/index.js';
import {
  measurementCleared,
  vertexAppended,
  vertexRemoved,
} from '../../store/slices/measureSlice.js';
import { parseCoordinates } from '../search/coordinates.js';

/**
 * The points of the measurement, and the way to add one without a pointer.
 *
 * Drawing a shape with a mouse has no keyboard equivalent, so a measurement
 * tool that only supports drawing is unusable without one. Typing a coordinate
 * appends a vertex through exactly the same action the sketch dispatches, which
 * makes the keyboard a real path rather than a token one.
 *
 * The parser is the one the Locate panel already uses: WGS84 only, validated to
 * Oklahoma, and it reorders longitude-first input when that is the only reading
 * that lands in the state. The reasons it does not accept State Plane, UTM or
 * NAD27 are recorded there and apply here unchanged.
 *
 * The numbers match the numbered markers drawn on the map, so a point in the
 * list can be found on the map and the reverse.
 */
export default function VertexList() {
  const dispatch = useAppDispatch();
  const vertices = useAppSelector(selectMeasureVertices);

  const [entry, setEntry] = useState('');
  const [error, setError] = useState<string | null>(null);

  const addPoint = () => {
    const point = parseCoordinates(entry);
    if (!point) {
      setError('Enter a latitude and longitude inside Oklahoma.');
      return;
    }
    setError(null);
    setEntry('');
    dispatch(vertexAppended(point));
  };

  return (
    <Box sx={{ px: 1.5, py: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography variant="h3" id="measure-points" sx={{ flex: 1, fontSize: '0.75rem' }}>
          {vertices.length === 1 ? '1 point' : `${vertices.length} points`}
        </Typography>
        {vertices.length > 0 && (
          <Button
            size="small"
            onClick={() => dispatch(measurementCleared())}
            sx={{ fontSize: '0.6875rem', py: 0, minWidth: 0 }}
          >
            Clear all
          </Button>
        )}
      </Box>

      {vertices.length > 0 && (
        <Box
          component="ul"
          aria-labelledby="measure-points"
          sx={{ listStyle: 'none', m: 0, mb: 1, p: 0, maxHeight: 132, overflow: 'auto' }}
        >
          {vertices.map((v, index) => (
            <Box
              component="li"
              // Coordinates repeat legitimately - a shape may return to a point
              // it already used - so position is what identifies a row.
              key={`${index}:${v.lng},${v.lat}`}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ width: 16 }}>
                {index + 1}
              </Typography>
              <Typography variant="caption" sx={{ flex: 1, fontVariantNumeric: 'tabular-nums' }}>
                {v.lat.toFixed(5)}, {v.lng.toFixed(5)}
              </Typography>
              <IconButton
                size="small"
                aria-label={`Remove point ${index + 1}`}
                onClick={() => dispatch(vertexRemoved(index))}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          size="small"
          value={entry}
          onChange={(e) => setEntry(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            addPoint();
          }}
          placeholder="35.4676, -97.5164"
          error={Boolean(error)}
          slotProps={{ htmlInput: { 'aria-label': 'Add a point by latitude and longitude' } }}
        />
        <Button variant="outlined" size="small" onClick={addPoint} disabled={!entry.trim()}>
          Add
        </Button>
      </Box>

      <Typography
        variant="caption"
        color={error ? 'error' : 'text.secondary'}
        sx={{ display: 'block', mt: 0.5 }}
      >
        {error ?? 'Latitude, longitude in WGS84. Escape cancels, Backspace removes the last point.'}
      </Typography>
    </Box>
  );
}
